import { LeakAdd } from "@mui/icons-material";
import { Chip } from "@mui/material";
import { startCase } from "lodash";
import { useEffect, useMemo, useState, type FC } from "react";
import { useFormContext, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";
import useEvent from "react-use-event-hook";
import { useRecoilValue, useSetRecoilState } from "recoil";

import { useDropdownSourceContext } from "@ryde/redux";
import {
  dropdownAtomFamily,
  isInnerItemEditingAtom,
  Log,
  type ICustomField,
} from "@ryde/shared";

import { RatioItem } from "./ratio-item";
import { RatioItemAdd } from "./ratio-item.add";
import { emptyRatio } from "./table-ratios.models";
import {
  TableRatiosItemsHeaderStyled,
  TableRatiosTemplateWrapperStyled,
} from "./table-ratios.styled";
import {
  buildSortedRatioEntries,
  normalizeTableRatiosFieldData,
} from "./table-ratios.utils";

export const TableRatiosTemplate: FC<ICustomField> = ({
  name,
  formLabel,
  formAttributes,
  formValidation,
}) => {
  const { control, getValues, setValue } = useFormContext();
  const { t } = useTranslation();
  const { getDropdownItems } = useDropdownSourceContext();
  const areaItems = useRecoilValue(dropdownAtomFamily("area"));
  const [editingCount, setEditingCount] = useState(0);
  const setIsInnerItemEditing = useSetRecoilState(isInnerItemEditingAtom);

  const handleEditStart = useEvent(() => {
    setEditingCount(prev => prev + 1);
  });

  const handleEditEnd = useEvent(() => {
    setEditingCount(prev => Math.max(0, prev - 1));
  });

  useEffect(() => {
    setIsInnerItemEditing(editingCount > 0);
  }, [editingCount, setIsInnerItemEditing]);

  useEffect(() => {
    return () => {
      setIsInnerItemEditing(false);
    };
  }, [setIsInnerItemEditing]);

  useEffect(() => {
    getDropdownItems("area");
  }, [getDropdownItems]);

  const ratiosListWatched = useWatch({ control, name });

  const sortedRatioEntries = useMemo(() => {
    const ratiosListValue = normalizeTableRatiosFieldData(
      ratiosListWatched,
      name,
      setValue,
    );
    Log.debug("Area ratios list normalized value", ratiosListValue);

    return buildSortedRatioEntries({ ratios: ratiosListValue, areaItems });
  }, [name, setValue, ratiosListWatched, areaItems]);

  const removeHandler = useEvent((formIndex: number) => {
    Log.debug("Remove area ratio handler fired for form index ", formIndex);

    const ratiosList = normalizeTableRatiosFieldData(
      getValues(name),
      name,
      setValue,
    );

    if (!ratiosList?.at(formIndex)) {
      Log.warnAndToast(
        t("errors.ITEM_DONT_EXISTS_IN_LIST"),
        formIndex,
        ratiosList,
      );
      return;
    }

    const ratiosListSpliced = ratiosList.toSpliced(formIndex, 1);
    setValue(name, ratiosListSpliced || [], {
      shouldDirty: true,
      shouldValidate: true,
    });
  });

  return (
    <TableRatiosTemplateWrapperStyled>
      <div
        className="table-ratios__container"
        data-inner-editing={editingCount > 0 ? "true" : undefined}
      >
        <div className="table-ratios-chip display-mode">
          <Chip label={t(`area.formLabel.ratios`)} icon={<LeakAdd />} />
        </div>
        {!!sortedRatioEntries?.length && (
          <TableRatiosItemsHeaderStyled className="display-mode">
            <p> {startCase(t("area.single"))}</p>
            <p> {t("general.FROM")}</p>
            <p> {t("general.TO")}</p>
            <p> {t("general.RATIO")}</p>
            <p> </p>
          </TableRatiosItemsHeaderStyled>
        )}

        {!!sortedRatioEntries?.length &&
          sortedRatioEntries.map(({ item: ratioItem, formIndex }) => {
            const uniqueKeyValue = `${formIndex}_${ratioItem.areaId}_${ratioItem?.from || 0}_${ratioItem?.to || 0}_${ratioItem?.ratio || 0}`;
            return (
              <RatioItem
                key={uniqueKeyValue}
                fieldName={name}
                control={control}
                index={formIndex}
                setValue={setValue}
                getValues={getValues}
                value={ratioItem || emptyRatio}
                removeHandler={removeHandler}
                onEditStart={handleEditStart}
                onEditEnd={handleEditEnd}
              />
            );
          })}
        <RatioItemAdd
          fieldName={name}
          index={sortedRatioEntries?.length || 0}
          setValue={setValue}
          getValues={getValues}
          onEditStart={handleEditStart}
          onEditEnd={handleEditEnd}
        />
      </div>
    </TableRatiosTemplateWrapperStyled>
  );
};
